import type { ChangeEvent } from 'react';
import { Button, IconButton, InputAdornment, Stack } from '@mui/material';
import { FloatingLabelInputLayout } from 'framework/ui/layout';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import { fieldTypeOf } from '../../field-types';
import { hasOptions } from '../../field-types/card';
import type { FieldElement } from '../../format/form-definition';
import type { Option } from '../../format/pieces';
import { useBuilder } from '../context';
import { DrawerSection } from '../drawer/DrawerSection';
import { createElementIdGenerator, optionsOf } from '../../format/elements';

export const OptionsSection = ({ field }: { field: FieldElement }) => {
  if (!hasOptions(fieldTypeOf(field).valueKind)) return null;
  return <DrawerSection sections={[{ id: 'options', title: 'Options', content: <OptionLines field={field} /> }]} />;
};

const OptionLines = ({ field }: { field: FieldElement }) => {
  const { form, edit } = useBuilder();
  const options = optionsOf(field);
  const setOptions = (next: Option[]) =>
    edit({ kind: 'setFieldSettings', fieldId: field.id, settings: { options: next } });
  const add = () => setOptions([...options, { id: createElementIdGenerator(form)('opt'), label: '' }]);
  const remove = (index: number) => setOptions(options.toSpliced(index, 1));

  return (
    <Stack useFlexGap sx={{ alignItems: 'flex-start' }}>
      {options.map((option, index) => (
        <FloatingLabelInputLayout
          key={option.id}
          source={`${field.id}-${option.id}`}
          value={option.label}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label={`Delete option ${index + 1}`}
                    disabled={options.length === 1}
                    onClick={() => remove(index)}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
            htmlInput: {
              'aria-label': `Option ${index + 1}`,
              onChange: (event: ChangeEvent<HTMLInputElement>) =>
                setOptions(options.with(index, { ...option, label: event.currentTarget.value })),
            },
          }}
        />
      ))}
      <Button variant="outlined" size="small" startIcon={<Add />} onClick={add}>
        Add option
      </Button>
    </Stack>
  );
};
